import BlockState from '@/frontend/pages/Dashboard/Workflows/BlockState'
import { displayDate, displayDuration } from '@/frontend/pages/Dashboard/Workflows/presentation'
import type { Copy, Filters, StateEnvelope, Summary } from '@/frontend/pages/Dashboard/Workflows/types'
import { cn } from '@/lib/utils'

export default function SummaryMetrics({ state, filters, workflow, copy }: { state: StateEnvelope<Summary> | undefined; filters: Filters; workflow?: boolean; copy: Copy }) {
  const summary = state?.data
  const missing = copy.shared.not_informed
  const rate = summary?.completion_rate == null ? missing : new Intl.NumberFormat('pt-BR', { style: 'percent', maximumFractionDigits: 0 }).format(summary.completion_rate)
  const metrics = workflow ? [
    { label: copy.metrics.total_runs, value: summary?.total_runs ?? missing },
    { label: copy.metrics.completion_rate, value: rate },
    { label: copy.metrics.typical_duration, value: displayDuration(summary?.typical_duration_ms, copy) },
  ] : [
    { label: copy.metrics.running_now, value: summary?.running_now ?? missing, dot: 'bg-brand' },
    { label: copy.metrics.failed_last_24h, value: summary?.failed_last_24h ?? missing, dot: 'bg-destructive' },
    { label: copy.metrics.total_runs, value: summary?.total_runs ?? missing },
    { label: copy.metrics.completion_rate, value: rate },
  ]
  return <section className="space-y-3" aria-label={copy.metrics.title} aria-busy={!state}>
    {!summary ? <BlockState loading={!state} error={state?.error} copy={copy} /> : <>
      <dl className={cn('grid border-y', workflow ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 lg:grid-cols-4')}>
        {metrics.map((metric, index) => <div key={metric.label} className={cn('space-y-1.5 py-[18px] px-5 first:pl-0', index > 0 && 'border-l', !workflow && index === 2 && 'max-lg:border-l-0 max-lg:pl-0')}>
          <dd className="flex items-center gap-2 text-[28px] font-semibold leading-[34px] tracking-tight">{'dot' in metric && <span className={cn('size-[7px] shrink-0 rounded-full', metric.dot)} aria-hidden />}{metric.value}</dd>
          <dt className="text-xs leading-4 text-muted-foreground">{metric.label}</dt>
        </div>)}
      </dl>
      <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
        <p>{displayDate(filters.started_from, missing)} → {displayDate(filters.started_to, missing)} · {copy.metrics.hint}</p>
        {summary.completion_rate !== null && <p>{copy.metrics.denominator.replace('%{completed}', String(summary.counts_by_status.completed)).replace('%{terminal}', String(summary.terminal_runs))}</p>}
        {workflow && summary.duration_sample && <p>{copy.metrics.sample.replace('%{size}', String(summary.duration_sample.sample_size)).replace('%{limit}', String(summary.duration_sample.limit))}</p>}
        {summary.unavailable_fields && Object.keys(summary.unavailable_fields).length > 0 && <p>{copy.metrics.unavailable}</p>}
      </div>
    </>}
  </section>
}
